import { isNotNullish, isNullish } from '@nx/stdlib';

import type {
  CreateToolRequest,
  ToolResponse,
  TransformedCreateToolBody,
  TransformedToolExtraParams,
  TransformedUpdateToolBody,
  UpdateToolRequest,
} from '../api-types/tool';
import type { BaseTool, Tool } from '../types/tool';

const serializeToolResponse = (tool: ToolResponse): Tool => {
  const transformedResponse: BaseTool = {
    id: tool.id,
    name: tool.name,
    description: tool.description,
    enabled: tool.enabled,
    type: tool.type,
  };

  if (tool.type === 'cypherTemplate') {
    if (isNullish(tool.extra_params?.template)) {
      throw new Error('Template is required');
    }

    return {
      ...transformedResponse,
      // Needs repeating for type assertion
      type: tool.type,
      extraParams: {
        template: tool.extra_params.template,
        parameters:
          tool.extra_params.parameters?.map((param) => ({
            name: param.name,
            dataType: param.data_type,
            description: param.description,
          })) ?? [],
      },
    };
  }

  if (tool.type === 'similaritySearch') {
    if (isNullish(tool.extra_params)) {
      throw new Error('Extra params are required');
    }

    if (isNullish(tool.extra_params.index)) {
      throw new Error('Index is required');
    }

    if (isNullish(tool.extra_params.top_k)) {
      throw new Error('Top K is required');
    }

    if (isNullish(tool.extra_params.provider)) {
      throw new Error('Provider is required');
    }

    if (isNullish(tool.extra_params.model)) {
      throw new Error('Model is required');
    }

    return {
      ...transformedResponse,
      type: tool.type,
      extraParams: {
        index: tool.extra_params.index,
        topK: tool.extra_params.top_k,
        provider: tool.extra_params.provider,
        model: tool.extra_params.model,
      },
    };
  }

  // To be used as catch all
  return {
    ...transformedResponse,
    type: 'text2cypher',
  };
};

export const transformListToolsResponse = (response: ToolResponse[]): Tool[] => {
  return response.map((tool) => serializeToolResponse(tool));
};

export const transformCreateToolRequest = (request: CreateToolRequest): TransformedCreateToolBody => {
  const baseBody = {
    name: request.name,
    description: request.description,
    enabled: request.enabled,
    type: request.type,
  };

  if (request.type === 'cypherTemplate') {
    return {
      ...baseBody,
      type: request.type,
      extra_params: {
        template: request.extraParams.template,
        parameters:
          request.extraParams.parameters?.map((param) => ({
            name: param.name,
            data_type: param.dataType,
            description: param.description,
          })) ?? [],
      },
    };
  }

  if (request.type === 'similaritySearch') {
    return {
      ...baseBody,
      type: request.type,
      extra_params: {
        provider: request.extraParams.provider,
        model: request.extraParams.model,
        index: request.extraParams.index,
        top_k: request.extraParams.topK,
      },
    };
  }

  return {
    ...baseBody,
    type: request.type,
  };
};

export const transformToolResponse = (response: ToolResponse): Tool => serializeToolResponse(response);

export const transformUpdateToolRequest = (request: UpdateToolRequest): TransformedUpdateToolBody => {
  const baseBody = {
    name: request.name,
    description: request.description,
    enabled: request.enabled,
  };

  if (request.extraParams) {
    const extraParams: TransformedToolExtraParams = {};

    if (isNotNullish(request.extraParams.template)) {
      extraParams.template = request.extraParams.template;
    }

    if (isNotNullish(request.extraParams.parameters)) {
      extraParams.parameters = request.extraParams.parameters.map((param) => ({
        name: param.name,
        data_type: param.dataType,
        description: param.description,
      }));
    }

    if (isNotNullish(request.extraParams.provider)) {
      extraParams.provider = request.extraParams.provider;
    }

    if (isNotNullish(request.extraParams.model)) {
      extraParams.model = request.extraParams.model;
    }

    if (isNotNullish(request.extraParams.index)) {
      extraParams.index = request.extraParams.index;
    }

    if (isNotNullish(request.extraParams.topK)) {
      extraParams.top_k = request.extraParams.topK;
    }

    return {
      ...baseBody,
      extra_params: extraParams,
    };
  }

  return baseBody;
};
